import { Controller } from "@hotwired/stimulus"

// Deck builder state controller.
// Owns deckState, renders deck area, handles all card interactions.
// Per-copy variant support: each copy tracks its own currentCardId.
export default class extends Controller {
  static targets = [
    "placeholder",
    "slotCard", "addedCard",
    "undoList",
    "cardSettingsModal",
    "variantSection", "modalVariantSet", "modalVariantOption",
    "modalDungeonButton",
    "modalDivineEpiphanyCard", "modalRegularEpiphanyCard",
    "modalEpiphanySearch",
    "regularEpiphanySection",
    "cardSettingsPreview",
    "faintMemoryScore",
    "budgetCount",
    "stylePickerModal", "stylePreview", "stylePreviewLabel", "styleOption",
    "leftColumnExtras", "addCardButton", "cardCount", "removeCount",
    "modalVariantsContainer",
    "engravingModal", "engravingPreview",
    "engravingSlot1Card", "engravingSlot2Card",
    "engravingSearch",
    "equipmentEnhancementModal", "enhancementSeasonTab", "enhancementSeason1Panel",
    "enhancementSeason2Panel", "equipmentEnhancementCard",
    "equipmentEnhancementSearch", "enhancementSummary",
    "enhancementTrigger", "matchCharacterBtn"
  ]

  static values = {
    existingState: Object
  }

  connect() {
    this.deckState = {
      characterId: null,
      slots: [],
      addedCards: [],
      equipmentEnhancements: {}
    }

    this.enhancementModalState = {}
    this.engravingModalState = {}
    this.variantTargetSlotOriginalId = null
    this.variantTargetCopyIndex = null

    window.deckState = this.deckState

    // Store bound references so they can be properly removed
    this._handleCardAdded = this.handleCardAdded.bind(this)
    this._handleCharacterSelected = this.handleCharacterSelected.bind(this)
    this._onKeydown = this.onKeydown.bind(this)

    document.addEventListener("keydown", this._onKeydown)
    document.addEventListener("card-picker:cardAdded", this._handleCardAdded)
    document.addEventListener("character-picker:characterSelected", this._handleCharacterSelected)

    this.modalState = {
      originalCardId: null,
      copyIndex: null,
      cardType: null,
      isVariant: false,
      selectedVariantId: null,
      selectedDungeon: null,
      divineEpiphanyId: null,
      epiphanyId: null
    }

    if (this.existingStateValue && this.existingStateValue.characterId) {
      this.loadExistingState(this.existingStateValue)
    }
  }

  async loadExistingState(state) {
    if (this._loadingCharacter) return
    if (this.slotCardTargets.length > 0) return
    this._loadingCharacter = true

    try {
      const characterId = state.characterId
      const response = await fetch(`/save_decks/cards_for_character?character_id=${characterId}`)
      const data = await response.json()

      this.placeholderTarget.insertAdjacentHTML("afterend", data.slot_cards)
      this.modalVariantsContainerTarget.innerHTML = data.modal_variants

      await new Promise(resolve => requestAnimationFrame(resolve))

      this.initializeSlotsForCharacter(characterId)

      // Mark removed slots in existing deckState
      state.slots.filter(s => s.removed).forEach(existingSlot => {
        const slot = this.deckState.slots.find(s => s.originalCardId === existingSlot.originalCardId)
        if (slot) {
          slot.removed = true
          slot.copyCards = []
          slot.cardName = existingSlot.cardName || null
        } else {
          this.deckState.slots.push({
            slotIndex: this.deckState.slots.length,
            originalCardId: existingSlot.originalCardId,
            cardName: existingSlot.cardName || null,
            origin: null,
            unique: false,
            removed: true,
            copyCards: []
        })
      }
    })

    // Restore slot state
    state.slots.filter(s => !s.removed).forEach(existingSlot => {
      const slot = this.deckState.slots.find(s => s.originalCardId === existingSlot.originalCardId)
      if (!slot) return
      slot.copyCards = existingSlot.copyCards.map(copy => ({
        copyIndex: copy.copyIndex,
        currentCardId: copy.currentCardId,
        divineEpiphanyId: copy.divineEpiphanyId,
        divineEpiphanyType: copy.divineEpiphanyType,
        epiphanyId: copy.epiphanyId,
        epiphanyType: copy.epiphanyType
      }))
    })

    // Pre-fetch categories needed for added cards
    const allowedCategories = ["season", "common", "monster"]
    const neededCategories = [...new Set(state.addedCards.map(c => c.category).filter(c => c && allowedCategories.includes(c)))]
    const cardPickerEl = document.querySelector("[data-controller='card-picker']")
    const cardPickerController = cardPickerEl
      ? this.application.getControllerForElementAndIdentifier(cardPickerEl, "card-picker")
      : null

    if (cardPickerController && neededCategories.length > 0) {
      await Promise.all(neededCategories.map(async category => {
        if (cardPickerController.categoryCache[category]) return
        const response = await fetch(`/save_decks/picker_cards?category=${category}`)
        const html = await response.text()
        cardPickerController.categoryCache[category] = html
        cardPickerController.outsideCardsContainerTarget.innerHTML += html
      }))
      await new Promise(resolve => requestAnimationFrame(resolve))
    }

    // Restore added cards
    this.deckState.addedCards = (state.addedCards || []).map(added => ({
      cardId: added.cardId,
      category: added.category,
      rarity: added.rarity,
      canEpiphany: added.canEpiphany,
      canEngrave: added.canEngrave === true || added.canEngrave === "true",
      cardType: added.cardType,
      unique: added.unique || false,
      copyCards: (added.copyCards || []).map(copy => ({
        copyIndex: copy.copyIndex,
        divineEpiphanyId: copy.divineEpiphanyId || null,
        divineEpiphanyType: copy.divineEpiphanyType || null,
        epiphanyId: copy.epiphanyId || null,
        epiphanyType: copy.epiphanyType || null,
        engraving1Id: copy.engraving1Id || null,
        engraving2Id: copy.engraving2Id || null
      }))
    }))

    state.addedCards.forEach(added => {
      added.copyCards.forEach(copy => {
        this.createAddedCardElement(added.cardId, copy.copyIndex, added.category, added.rarity, added.unique)
      })
    })

    // Restore style
    if (state.styleKey) {
      const styleOption = this.styleOptionTargets.find(o => o.dataset.styleKey === state.styleKey)
      if (styleOption) {
        const assetPath = styleOption.dataset.assetPath
        this.stylePreviewTarget.style.backgroundImage = `url('${assetPath}')`
        this.stylePreviewLabelTarget.classList.add("hidden")
        document.getElementById("style-key-input").value = state.styleKey
        styleOption.classList.add("selected")
      }
    } else if (state.customStyleUrl) {
      this.stylePreviewTarget.style.backgroundImage = `url('${state.customStyleUrl}')`
      this.stylePreviewLabelTarget.classList.add("hidden")
    }

    this.render()
    this.leftColumnExtrasTarget.classList.remove("hidden")

    // Show add card button
    const trigger = document.querySelector(".card-picker-trigger")
    if (trigger) trigger.classList.remove("hidden")

    // Restore equipment
    if (state.weaponId || state.armorId || state.accessoryId) {
      await new Promise(resolve => requestAnimationFrame(resolve))

      this.element.querySelectorAll("[data-controller='equipment-picker']").forEach(picker => {
        const pickerController = this.application.getControllerForElementAndIdentifier(picker, "equipment-picker")
        if (!pickerController) return

        const field = picker.querySelector("[data-equipment-picker-target='input']")
        if (!field) return

        const fieldName = field.name
        if (fieldName.includes("weapon_id") && state.weaponId) {
          pickerController.selectById(state.weaponId)
        } else if (fieldName.includes("armor_id") && state.armorId) {
          pickerController.selectById(state.armorId)
        } else if (fieldName.includes("accessory_id") && state.accessoryId) {
          pickerController.selectById(state.accessoryId)
        }
      })
    }

      this.deckState.equipmentEnhancements = state.equipmentEnhancements || {}
      await new Promise(resolve => requestAnimationFrame(resolve))
      this.updateEnhancementIcons()

    } finally {
      this._loadingCharacter = false
    }
  }

  disconnect() {
    document.removeEventListener("card-picker:cardAdded", this._handleCardAdded)
    document.removeEventListener("character-picker:characterSelected", this._handleCharacterSelected)
    document.removeEventListener("keydown", this._onKeydown)
    delete window.deckState
  }

  onKeydown(e) {
    if (e.key !== "Escape") return

    if (this.hasCardSettingsModalTarget && !this.cardSettingsModalTarget.classList.contains("hidden")) {
      this.cancelCardSettings()
      return
    }

    if (this.hasEngravingModalTarget && !this.engravingModalTarget.classList.contains("hidden")) {
      const searchEl = this.engravingModalTarget.querySelector("[data-controller='engraving-search']")
      if (searchEl) {
        const searchController = this.application.getControllerForElementAndIdentifier(searchEl, "engraving-search")
        if (searchController?.enabled) return
      }
      this.cancelEngravingSettings()
      return
    }

    if (this.hasStylePickerModalTarget && !this.stylePickerModalTarget.classList.contains("hidden")) {
      this.closeStylePicker()
      return
    }

    if (this.hasEquipmentEnhancementModalTarget && !this.equipmentEnhancementModalTarget.classList.contains("hidden")) {
      this.cancelEquipmentEnhancement()
      return
    }
  }

  // ----- Event handlers -----

  async handleCharacterSelected(event) {
      const characterId = parseInt(event.detail.characterId, 10)
      const characterJobs = event.detail.characterJobs || ""
      const characterElement = event.detail.characterElement || ""
      this.element.dataset.deckCharacterJobs = characterJobs
      this.element.dataset.deckCharacterElement = characterElement
      if (this._loadingCharacter) return

      this._loadingCharacter = true

      this.slotCardTargets.forEach(el => {
        const video = el.querySelector("video")
        if (video) {
          video.pause()
          video.src = ""
          video.load()
        }
        el.remove()
      })

      this.addedCardTargets.forEach(el => {
        const video = el.querySelector("video")
        if (video) {
          video.pause()
          video.src = ""
          video.load()
        }
        el.remove()
      })
      this.deckState.addedCards = []

      try {
        const response = await fetch(`/save_decks/cards_for_character?character_id=${characterId}`)
        const data = await response.json()

        this.placeholderTarget.insertAdjacentHTML("afterend", data.slot_cards)
        this.modalVariantsContainerTarget.innerHTML = data.modal_variants

        await new Promise(resolve => requestAnimationFrame(resolve))

        this.initializeSlotsForCharacter(characterId)
        this.render()
        this.leftColumnExtrasTarget.classList.remove("hidden")
      } finally {
        this._loadingCharacter = false
      }
  }

  handleCardAdded(event) {
    const cardId = parseInt(event.detail.cardId, 10)
    const cardCategory = event.detail.cardCategory

    if (cardCategory === "combatant") {
      this.addCombatantCopy(cardId)
    } else {
      this.addOutsideCard(cardId)
    }

    this.render()
  }

  // ----- Button actions -----

  removeCard(event) {
    event.stopPropagation()
    const wrapper = event.currentTarget.closest("[data-original-card-id]")
    const originalCardId = parseInt(wrapper.dataset.originalCardId, 10)
    const copyIndex = parseInt(wrapper.dataset.copyIndex, 10)
    const cardType = wrapper.dataset.cardType

    if (cardType === "slot") {
      this.removeSlotCopy(originalCardId, copyIndex)
      this.render()
    } else {
      const cardId = parseInt(wrapper.dataset.cardId, 10)
      this.removeAddedCopy(cardId, copyIndex)
      requestAnimationFrame(() => {
        this.renderFaintMemory()
        this.renderBudgetCount()
        this.updateCardEpiphanyVisuals()
        this.updateCardEngravingVisuals()
        this.updateAddedCardBorders()
        const cardCount = this.deckState.slots
                            .filter(s => !s.removed)
                            .reduce((sum, slot) => sum + slot.copyCards.length, 0) +
                          this.deckState.addedCards.reduce((sum, c) => sum + c.copyCards.length, 0)
        if (this.hasCardCountTarget) this.cardCountTarget.textContent = cardCount
      })
    }
  }

  duplicateCard(event) {
    event.stopPropagation()
    const wrapper = event.currentTarget.closest("[data-original-card-id]")
    const originalCardId = parseInt(wrapper.dataset.originalCardId, 10)
    const copyIndex = parseInt(wrapper.dataset.copyIndex, 10)

    this.duplicateSlotCopy(originalCardId, copyIndex)
    this.render()
  }

  submitDeck(event) {
    event.preventDefault()

    if (!this.deckState.characterId) {
      alert("Please select a character before saving.")
      return
    }

    const nameField = document.getElementById("save_deck_name")
    if (!nameField || !nameField.value.trim()) {
      alert("Please enter a deck name before saving.")
      nameField?.focus()
      return
    }

    const activeCopies = this.deckState.slots
      .filter(s => !s.removed)
      .reduce((sum, s) => sum + s.copyCards.length, 0)

    if (activeCopies === 0) {
      alert("Your deck has no cards.")
      return
    }

    const faintMemory = this.calculateFaintMemory()

    const payload = {
      characterId: this.deckState.characterId,
      faintMemory,
      slots: this.deckState.slots,
      addedCards: this.deckState.addedCards,
      equipmentEnhancements: this.deckState.equipmentEnhancements || {}
    }

    const hiddenField = document.getElementById("deck_state_json")
    if (!hiddenField) {
      console.error("[deck] Hidden field #deck_state_json not found")
      return
    }

    hiddenField.value = JSON.stringify(payload)
    // Remove the debug log and return
    event.currentTarget.closest("form").submit()
  }

  // ----- Epiphany picker -----

  openEpiphanyPicker(event) {
    event.stopPropagation()
    const wrapper = event.currentTarget.closest("[data-original-card-id]")
    const originalCardId = parseInt(wrapper.dataset.originalCardId, 10)
    const copyIndex = parseInt(wrapper.dataset.copyIndex, 10)
    const cardCardType = wrapper.dataset.cardCardType

    // Store which slot + copy we're working with
    this.epiphanyTargetSlotOriginalId = originalCardId
    this.epiphanyTargetCopyIndex = copyIndex
    this.epiphanyTargetCardType = cardCardType

    // Filter epiphanies by card type
    this.filterEpiphaniesByCardType(cardCardType)

    // Select the first dungeon by default
    const firstDungeon = this.dungeonButtonTargets[0]
    if (firstDungeon) {
      this.activateDungeon(firstDungeon)
    }

    // Clear search
    if (this.hasEpiphanySearchTarget) {
      this.epiphanySearchTarget.value = ""
    }

    this.epiphanyModalTarget.classList.remove("hidden")
  }

  closeEpiphanyPicker(event) {
    if (event.target === this.epiphanyModalTarget) {
      this.epiphanyModalTarget.classList.add("hidden")
    }
  }

  closeEpiphanyPickerBtn() {
    this.epiphanyModalTarget.classList.add("hidden")
  }

  selectDungeon(event) {
    this.activateDungeon(event.currentTarget)
  }

  activateDungeon(button) {
    // Deactivate all dungeon buttons
    this.dungeonButtonTargets.forEach(btn => {
      btn.classList.remove("epiphany-dungeon-btn--active")
    })

    // Activate clicked button
    button.classList.add("epiphany-dungeon-btn--active")

    // Store selected dungeon
    this.selectedDungeon = button.dataset.dungeon
  }

  selectEpiphany(event) {
    const card = event.currentTarget
    const epiphanyId = parseInt(card.dataset.epiphanyId, 10)
    const epiphanyType = card.dataset.epiphanyType  // "divine" or "regular"
    const epiphanyEffect = card.dataset.epiphanyEffect

    if (!this.selectedDungeon) {
      console.warn("[deck] No dungeon selected")
      return
    }

    // Assign epiphany to the target copy
    this.assignEpiphany(
      this.epiphanyTargetSlotOriginalId,
      this.epiphanyTargetCopyIndex,
      epiphanyType === "divine" ? "divine" : "regular",
      epiphanyId,
      this.selectedDungeon
    )

    this.epiphanyModalTarget.classList.add("hidden")
    this.updateCardEpiphanyVisuals()
  }

  filterEpiphaniesByCardType(cardType) {
    this.epiphanyCardTargets.forEach(card => {
      const epiphanyCardType = card.dataset.cardType
      const matches = epiphanyCardType === cardType ||
                      epiphanyCardType === "universal"

      if (matches) {
        card.classList.remove("type-hidden")
        card.classList.remove("hidden")
      } else {
        card.classList.add("type-hidden")
        card.classList.add("hidden")
      }
    })
  }

  searchEpiphanies() {
    const searchTerm = this.epiphanySearchTarget.value.toLowerCase()

    this.epiphanyCardTargets.forEach(card => {
      // Never show cards hidden by type filter
      if (card.classList.contains("type-hidden")) return

      const effect = card.dataset.epiphanyEffect.toLowerCase()
      const matches = !searchTerm || effect.includes(searchTerm)
      card.classList.toggle("hidden", !matches)
    })
  }

  updateCardEpiphanyVisuals() {
    const dungeonIconMap = {}
    const itxtIconMap = {}

    // Try DOM first (works on new page)
    this.modalDungeonButtonTargets.forEach(btn => {
      const dungeon = btn.dataset.dungeon
      const overlayImg = btn.querySelector("[data-overlay]")
      const itxtImg = btn.querySelector(".card-settings-dungeon-icon")
      if (dungeon && overlayImg?.src) dungeonIconMap[dungeon] = overlayImg.src
      if (dungeon && itxtImg?.src) itxtIconMap[dungeon] = itxtImg.src
    })

    // Fallback to data attributes (works on edit page)
    const el = this.element
    ;['nihilum','diallos','secred','circen','vitor','caligo'].forEach(dungeon => {
      const itxtKey = `deckItxt${dungeon.charAt(0).toUpperCase() + dungeon.slice(1)}`
      const divineKey = `deckDivine${dungeon.charAt(0).toUpperCase() + dungeon.slice(1)}`
      if (!itxtIconMap[dungeon] && el.dataset[itxtKey]) itxtIconMap[dungeon] = el.dataset[itxtKey]
      if (!dungeonIconMap[dungeon] && el.dataset[divineKey]) dungeonIconMap[dungeon] = el.dataset[divineKey]
    })

    const defaultIcon = this.hasCardSettingsModalTarget
      ? this.cardSettingsModalTarget.dataset.defaultEpiphanyIcon
      : null

    this.deckState.slots.forEach(slot => {
      slot.copyCards.forEach(copy => {
        const cardEl = this.slotCardTargets.find(el => {
          return parseInt(el.dataset.originalCardId, 10) === slot.originalCardId &&
                parseInt(el.dataset.copyIndex, 10) === copy.copyIndex &&
                parseInt(el.dataset.cardId, 10) === copy.currentCardId &&
                !el.classList.contains("hidden")
        })

        if (!cardEl) return

        const baseCost = parseInt(cardEl.dataset.baseCost, 10)
        const divineCard = copy.divineEpiphanyId
          ? this.modalDivineEpiphanyCardTargets.find(c => parseInt(c.dataset.epiphanyId, 10) === copy.divineEpiphanyId)
          : null
        const costModifier = parseInt(divineCard?.dataset.costModifier || 0, 10)
        const newCost = isNaN(baseCost) ? null : Math.max(0, baseCost + costModifier)
        const costEl = cardEl.querySelector(".card-cost")
        if (costEl && newCost !== null) costEl.textContent = newCost

        const iconEl = cardEl.querySelector("[data-epiphany-icon]")
        const effectEl = cardEl.querySelector("[data-epiphany-effects]")

        if (!iconEl || !effectEl) return

        if (copy.divineEpiphanyId && copy.divineEpiphanyType) {
          const iconPath = dungeonIconMap[copy.divineEpiphanyType?.toLowerCase()]
          if (iconPath) {
            iconEl.innerHTML = `<img src="${iconPath}" class="epiphany-icon-img" alt="${copy.divineEpiphanyType}">`
            iconEl.classList.remove("hidden")
          }
        } else if (copy.epiphanyId && defaultIcon) {
          iconEl.innerHTML = `<img src="${defaultIcon}" class="epiphany-icon-img" alt="epiphany">`
          iconEl.classList.remove("hidden")
        } else if (copy.currentCardId !== slot.originalCardId && defaultIcon) {
          iconEl.innerHTML = `<img src="${defaultIcon}" class="epiphany-icon-img" alt="epiphany">`
          iconEl.classList.remove("hidden")
        } else {
          iconEl.innerHTML = ""
          iconEl.classList.add("hidden")
        }

        const effects = []

        if (copy.epiphanyId) {
          const regularCard = this.modalRegularEpiphanyCardTargets.find(
            c => parseInt(c.dataset.epiphanyId, 10) === copy.epiphanyId
          )
          if (regularCard) {
            effects.push(`<p class="epiphany-added-effect">${regularCard.dataset.epiphanyEffect}</p>`)
          }
        }

        if (copy.divineEpiphanyId && copy.divineEpiphanyType) {
          const divineCard = this.modalDivineEpiphanyCardTargets.find(
            c => parseInt(c.dataset.epiphanyId, 10) === copy.divineEpiphanyId
          )
          if (divineCard) {
            const itxtSrc = itxtIconMap[copy.divineEpiphanyType?.toLowerCase()] || ""
            effects.push(`
              <p class="epiphany-added-effect epiphany-added-effect--divine">
                <img src="${itxtSrc}" class="epiphany-itxt-icon" alt="${copy.divineEpiphanyType}">
                <span>${divineCard.dataset.epiphanyEffect.split('||').join(' ')}</span>
              </p>
            `)
          }
        }

          if (effects.length > 0) {
            effectEl.innerHTML = effects.join("")
            effectEl.classList.remove("hidden")
          } else {
            effectEl.innerHTML = ""
            effectEl.classList.add("hidden")
          }
        })
      })

      // Added cards epiphany visuals
      this.deckState.addedCards.forEach(added => {
        added.copyCards.forEach(copy => {
          const cardEl = this.addedCardTargets.find(el =>
            parseInt(el.dataset.cardId, 10) === added.cardId &&
            parseInt(el.dataset.copyIndex, 10) === copy.copyIndex &&
            !el.classList.contains("hidden")
          )
          if (!cardEl) return

          const iconEl = cardEl.querySelector("[data-epiphany-icon]")
          const effectEl = cardEl.querySelector("[data-epiphany-effects]")
          if (!iconEl || !effectEl) return

          if (copy.divineEpiphanyId && copy.divineEpiphanyType) {
            const iconPath = dungeonIconMap[copy.divineEpiphanyType?.toLowerCase()]
            if (iconPath) {
              iconEl.innerHTML = `<img src="${iconPath}" class="epiphany-icon-img" alt="${copy.divineEpiphanyType}">`
              iconEl.classList.remove("hidden")
            }
          } else if (copy.epiphanyId && defaultIcon) {
            iconEl.innerHTML = `<img src="${defaultIcon}" class="epiphany-icon-img" alt="epiphany">`
            iconEl.classList.remove("hidden")
          } else {
            iconEl.innerHTML = ""
            iconEl.classList.add("hidden")
          }

          const effects = []

          if (copy.epiphanyId) {
            const regularCard = this.modalRegularEpiphanyCardTargets.find(
              c => parseInt(c.dataset.epiphanyId, 10) === copy.epiphanyId
            )
            if (regularCard) {
              effects.push(`<p class="epiphany-added-effect">${regularCard.dataset.epiphanyEffect}</p>`)
            }
          }

          if (copy.divineEpiphanyId && copy.divineEpiphanyType) {
            const divineCard = this.modalDivineEpiphanyCardTargets.find(
              c => parseInt(c.dataset.epiphanyId, 10) === copy.divineEpiphanyId
            )
            if (divineCard) {
              const itxtSrc = itxtIconMap[copy.divineEpiphanyType?.toLowerCase()] || ""
              effects.push(`
                <p class="epiphany-added-effect epiphany-added-effect--divine">
                  <img src="${itxtSrc}" class="epiphany-itxt-icon" alt="${copy.divineEpiphanyType}">
                  <span>${divineCard.dataset.epiphanyEffect.split('||').join(' ')}</span>
                </p>
              `)
            }
          }

          if (effects.length > 0) {
            effectEl.innerHTML = effects.join("")
            effectEl.classList.remove("hidden")
          } else {
            effectEl.innerHTML = ""
            effectEl.classList.add("hidden")
          }
      })
    })
  }

  updateCardEngravingVisuals() {
    const luxPath = this.element.dataset.deckEidolonLux
    const umbraPath = this.element.dataset.deckEidolonUmbra
    const slotPath = this.element.dataset.deckEidolonSlot

    const iconPath = (eidolon) => {
      if (eidolon === "lux") return luxPath
      if (eidolon === "umbra") return umbraPath
      return slotPath
    }

    const getEngCard = (engravingId, slot) => {
      if (!engravingId) return null
      const targets = slot === "slot1" ? this.engravingSlot1CardTargets : this.engravingSlot2CardTargets
      return targets.find(c => parseInt(c.dataset.engravingId, 10) === engravingId) || null
    }

    this.deckState.addedCards.forEach(added => {
      if (!added.canEngrave) return
      added.copyCards.forEach(copy => {
        const cardEl = this.addedCardTargets.find(el =>
          parseInt(el.dataset.cardId, 10) === added.cardId &&
          parseInt(el.dataset.copyIndex, 10) === copy.copyIndex &&
          !el.classList.contains("hidden")
        )
        if (!cardEl) return

        const czn = cardEl.querySelector(".czn-card")
        if (!czn) return

        // Always remove slot icons and re-render (even if clearing)
        czn.querySelectorAll(".engraving-slot-icons").forEach(el => el.remove())

        const newCost = this.calculateEngravingCost(added.cardId, copy.engraving1Id, copy.engraving2Id)
        const costEl = czn.querySelector(".card-cost")
        if (costEl && newCost !== null) costEl.textContent = newCost

        const slot1Eidolon = copy.engraving1Id ? this.getEngravingEidolon(copy.engraving1Id, "slot1") : null
        const slot2Eidolon = copy.engraving2Id ? this.getEngravingEidolon(copy.engraving2Id, "slot2") : null

        // Restore slot icons (shows empty slots when both null)
        const slotIconsEl = document.createElement("div")
        slotIconsEl.className = "engraving-slot-icons"
        slotIconsEl.innerHTML = `
          <img src="${slotPath}" class="engraving-slot-base">
          <img src="${iconPath(slot1Eidolon)}" class="engraving-slot-overlay engraving-slot-top">
          <img src="${iconPath(slot2Eidolon)}" class="engraving-slot-overlay engraving-slot-bottom">
        `
        czn.appendChild(slotIconsEl)

        // Restore card name
        const nameEl = czn.querySelector(".card-name")
        if (nameEl) nameEl.textContent = this.personaName(slot1Eidolon, slot2Eidolon)

        // Restore illustration
        if (!copy.engraving1Id && !copy.engraving2Id) {
          // Both cleared — restore original illustration from picker card
          const pickerCard = document.querySelector(`[data-card-picker-target="card"][data-card-id="${added.cardId}"]`)
          if (pickerCard) {
            const originalIllustration = pickerCard.querySelector(".card-illustration")
            const illustrationEl = czn.querySelector(".card-illustration")
            if (originalIllustration && illustrationEl) {
              illustrationEl.src = originalIllustration.src
            }
          }
        } else {
          const illustrationKey = this.personaIllustrationKey(added.cardType, slot1Eidolon, slot2Eidolon)
          if (illustrationKey) {
            const dataKey = `deckPersona${illustrationKey.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join("")}`
            const illustrationEl = czn.querySelector(".card-illustration")
            if (illustrationEl && this.element.dataset[dataKey]) {
              illustrationEl.src = this.element.dataset[dataKey]
            }
          }
        }

        // Restore talent line from picker card original, then inject engraving talents
        const talentEl = czn.querySelector(".card-talent")
        if (talentEl) {
          const pickerCard = document.querySelector(`[data-card-picker-target="card"][data-card-id="${added.cardId}"]`)
          if (pickerCard) {
            const originalTalentEl = pickerCard.querySelector(".card-talent")
            if (originalTalentEl) talentEl.textContent = originalTalentEl.textContent
          }

          const engravingTalents = []
          ;["slot1", "slot2"].forEach(slot => {
            const id = slot === "slot1" ? copy.engraving1Id : copy.engraving2Id
            const engCard = getEngCard(id, slot)
            if (!engCard) return
            const talents = engCard.dataset.talents ? engCard.dataset.talents.split(",").filter(Boolean) : []
            engravingTalents.push(...talents)
          })

          const uniqueTalents = [...new Set(engravingTalents)]
          if (uniqueTalents.length > 0) {
            const current = talentEl.textContent.replace(/^\[|\]$/g, "").trim()
            const formatted = uniqueTalents.map(t => t.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()))
            talentEl.textContent = `[ ${current} / ${formatted.join(" / ")} ]`
          }
        }

        // Update effect text
        const effectEl = czn.querySelector("[data-epiphany-effects]")
        if (effectEl) {
          const effects = []
          ;["slot1", "slot2"].forEach(slot => {
            const id = slot === "slot1" ? copy.engraving1Id : copy.engraving2Id
            const engCard = getEngCard(id, slot)
            if (!engCard) return
            const eidolon = engCard.dataset.eidolon
            const effect = engCard.dataset.engravingEffect
            const eidolonIconPath = eidolon === "lux" ? luxPath : umbraPath
            effects.push(`
              <p class="engraving-added-effect">
                <img src="${eidolonIconPath}" class="engraving-effect-eidolon-icon">
                <span>${effect}</span>
              </p>
            `)
          })

          if (effects.length > 0) {
            effectEl.innerHTML = effects.join("")
            effectEl.classList.remove("hidden")
          } else {
            effectEl.innerHTML = ""
            effectEl.classList.add("hidden")
          }
        }
      })
    })
  }

  updateAddedCardBorders() {
    const characterElement = (this.element.dataset.deckCharacterElement || "").toLowerCase()
    if (!characterElement) return

    const borderPath = this.element.dataset[`deckBorder${characterElement.charAt(0).toUpperCase() + characterElement.slice(1)}`]
    if (!borderPath) return

    this.deckState.addedCards.forEach(added => {
      if (added.category !== "season" && added.category !== "common") return

      this.addedCardTargets
        .filter(el => parseInt(el.dataset.cardId, 10) === added.cardId)
        .forEach(el => {
          const borderEl = el.querySelector(".card-element-border")
          if (borderEl) borderEl.src = borderPath
        })
    })
  }
    selectVariant(event) {
    event.stopPropagation()
    const variantId = parseInt(event.currentTarget.dataset.variantId, 10)
    const originalCardId = this.variantTargetSlotOriginalId
    const copyIndex = this.variantTargetCopyIndex

    const slot = this.deckState.slots.find(s => s.originalCardId === originalCardId)
    if (!slot) return

    const copy = slot.copyCards.find(c => c.copyIndex === copyIndex)
    if (!copy) return

    copy.currentCardId = variantId

    this.variantModalTarget.classList.add("hidden")
    this.variantTargetSlotOriginalId = null
    this.variantTargetCopyIndex = null

    this.render()
  }

  restoreSlot(event) {
    event.stopPropagation()
    const originalCardId = parseInt(event.currentTarget.dataset.originalCardId, 10)

    const slot = this.deckState.slots.find(s => s.originalCardId === originalCardId)
    if (!slot) return

    slot.removed = false
    slot.copyCards = [{
      copyIndex: 1,
      currentCardId: originalCardId,
      divineEpiphanyId: null,
      divineEpiphanyType: null,
      epiphanyId: null,
      epiphanyType: null
    }]

    this.render()
  }

  // ----- Card Settings Modal -----

  openCardSettingsModal(event) {
    if (event.target.closest("button")) return

    const wrapper = event.currentTarget
    const originalCardId = parseInt(wrapper.dataset.originalCardId, 10)
    const copyIndex = parseInt(wrapper.dataset.copyIndex, 10)
    const cardCardType = wrapper.dataset.cardCardType
    const canEpiphany = wrapper.dataset.canEpiphany === "true"
    const canEngrave = wrapper.dataset.canEngrave === "true"
    const variantable = wrapper.dataset.variantable === "true"
    const cardType = wrapper.dataset.cardType

    // Persona cards use the engraving modal instead
    if (canEngrave) {
      this.openEngravingModal(event)
      return
    }

    if (!canEpiphany && !variantable) return

    // Find current copy state — slot card or added card
    let copy
    if (cardType === "added") {
      const added = this.deckState.addedCards.find(c => c.cardId === originalCardId)
      if (!added) return
      copy = added.copyCards.find(c => c.copyIndex === copyIndex)
      if (!copy) return
    } else {
      const slot = this.deckState.slots.find(s => s.originalCardId === originalCardId)
      if (!slot) return
      copy = slot.copyCards.find(c => c.copyIndex === copyIndex)
      if (!copy) return
    }

    this.modalState = {
      originalCardId,
      copyIndex,
      cardType: cardCardType,
      sourceType: cardType,
      isVariant: cardType === "slot" && copy.currentCardId !== originalCardId,
      selectedVariantId: cardType === "slot" ? (copy.currentCardId || originalCardId) : originalCardId,
      selectedDungeon: copy.divineEpiphanyType || null,
      divineEpiphanyId: copy.divineEpiphanyId || null,
      epiphanyId: copy.epiphanyId || null
    }

    // Always hide variants for added cards
    if (variantable) {
      this.variantSectionTarget.classList.remove("hidden")
      this.modalVariantSetTargets.forEach(set => {
        const setBaseId = parseInt(set.dataset.baseCardId, 10)
        set.classList.toggle("hidden", setBaseId !== originalCardId)
      })
      this.modalVariantOptionTargets.forEach(opt => {
        const variantId = parseInt(opt.dataset.variantId, 10)
        opt.classList.toggle("modal-variant-selected", variantId === copy.currentCardId)
      })
    } else {
      this.variantSectionTarget.classList.add("hidden")
    }

    this.filterModalEpiphaniesByCardType(cardCardType)

    if (copy.divineEpiphanyType) {
      const dungeonBtn = this.modalDungeonButtonTargets.find(
        b => b.dataset.dungeon === copy.divineEpiphanyType
      )
      if (dungeonBtn) this.activateModalDungeon(dungeonBtn)
    } else {
      const firstDungeon = this.modalDungeonButtonTargets[0]
      if (firstDungeon) this.activateModalDungeon(firstDungeon)
    }

    this.modalDivineEpiphanyCardTargets.forEach(card => {
      const id = parseInt(card.dataset.epiphanyId, 10)
      card.classList.toggle("modal-epiphany-selected", id === copy.divineEpiphanyId)
    })

    this.modalRegularEpiphanyCardTargets.forEach(card => {
      const id = parseInt(card.dataset.epiphanyId, 10)
      card.classList.toggle("modal-epiphany-selected", id === copy.epiphanyId)
    })

    this.updateRegularEpiphanyAvailability()

    if (this.hasModalEpiphanySearchTarget) {
      this.modalEpiphanySearchTarget.value = ""
    }

    this.renderCardSettingsPreview()
    this.cardSettingsModalTarget.classList.remove("hidden")
  }

  cancelCardSettings() {
    this.cardSettingsModalTarget.classList.add("hidden")
    this.modalState = {}
  }

  // ============================================
  // ENGRAVING MODAL
  // ============================================

  openEngravingModal(event) {
    if (event.target.closest("button")) return

    const wrapper = event.currentTarget
    const cardId = parseInt(wrapper.dataset.cardId, 10)
    const copyIndex = parseInt(wrapper.dataset.copyIndex, 10)
    const cardType = wrapper.dataset.cardCardType

    const added = this.deckState.addedCards.find(c => c.cardId === cardId)
    if (!added) return
    const copy = added.copyCards.find(c => c.copyIndex === copyIndex)
    if (!copy) return

    this.engravingModalState = {
      cardId,
      copyIndex,
      cardType,
      slot1EngravingId: copy.engraving1Id || null,
      slot2EngravingId: copy.engraving2Id || null
    }

    this._matchCharacter = true
    this.matchCharacterBtnTarget.classList.add("match-combatant-btn--active")
    const characterJobs = (this.element.dataset.deckCharacterJobs || "").toLowerCase().split(",").filter(Boolean)
    this.filterEngravings(cardType, characterJobs, true)
    this.highlightEngravingSelections()
    this.renderEngravingPreview()

    const engravingSearchEl = this.engravingModalTarget.querySelector("[data-controller='engraving-search']")
    if (engravingSearchEl) {
      const engravingSearch = this.application.getControllerForElementAndIdentifier(engravingSearchEl, "engraving-search")
      if (engravingSearch?.enabled) engravingSearch.reset()
    }

    this.engravingModalTarget.classList.remove("hidden")
  }

  cancelEngravingSettings() {
    this.engravingModalTarget.classList.add("hidden")
    this.engravingModalState = {}
  }

  clickOutsideEngravingModal(event) {
    if (event.target === this.engravingModalTarget) {
      this.cancelEngravingSettings()
    }
  }

  filterEngravings(cardType, characterJobs, matchCharacter = true) {
    const normalizedCharacterJobs = characterJobs.map(j => j.toLowerCase())
    ;[...this.engravingSlot1CardTargets, ...this.engravingSlot2CardTargets].forEach(card => {
      const engCardType = card.dataset.cardType
      const engJobs = card.dataset.jobs ? card.dataset.jobs.split(",").filter(Boolean) : []
      const typeMatch = engCardType === cardType
      const jobMatch = !matchCharacter ||
                      engJobs.length === 0 ||
                      engJobs.some(j => normalizedCharacterJobs.includes(j.toLowerCase()))

      // Type mismatch always hides
      if (!typeMatch) {
        card.classList.add("hidden", "job-hidden")
        return
      }

      // Job filter uses job-hidden so search doesn't override it
      if (!jobMatch) {
        card.classList.add("hidden", "job-hidden")
      } else {
        card.classList.remove("hidden", "job-hidden")
      }
    })
  }

  highlightEngravingSelections() {
    const { slot1EngravingId, slot2EngravingId } = this.engravingModalState

    this.engravingSlot1CardTargets.forEach(card => {
      const id = parseInt(card.dataset.engravingId, 10)
      card.classList.toggle("modal-epiphany-selected", id === slot1EngravingId)
    })

    this.engravingSlot2CardTargets.forEach(card => {
      const id = parseInt(card.dataset.engravingId, 10)
      card.classList.toggle("modal-epiphany-selected", id === slot2EngravingId)
    })
  }

  selectEngravingSlot1(event) {
    event.stopPropagation()
    const id = parseInt(event.currentTarget.dataset.engravingId, 10)
    this.engravingModalState.slot1EngravingId = this.engravingModalState.slot1EngravingId === id ? null : id
    this.highlightEngravingSelections()
    this.renderEngravingPreview()
  }

  selectEngravingSlot2(event) {
    event.stopPropagation()
    const id = parseInt(event.currentTarget.dataset.engravingId, 10)
    this.engravingModalState.slot2EngravingId = this.engravingModalState.slot2EngravingId === id ? null : id
    this.highlightEngravingSelections()
    this.renderEngravingPreview()
  }

  confirmEngravingSettings() {
    const { cardId, copyIndex, slot1EngravingId, slot2EngravingId } = this.engravingModalState

    const added = this.deckState.addedCards.find(c => c.cardId === cardId)
    if (!added) return
    const copy = added.copyCards.find(c => c.copyIndex === copyIndex)
    if (!copy) return

    copy.engraving1Id = slot1EngravingId || null
    copy.engraving2Id = slot2EngravingId || null

    this.engravingModalTarget.classList.add("hidden")
    this.engravingModalState = {}
    this.render()
  }

  searchEngravings() {
    const term = this.engravingSearchTarget.value.toLowerCase()
    ;[...this.engravingSlot1CardTargets, ...this.engravingSlot2CardTargets].forEach(card => {
      if (card.classList.contains("job-hidden")) return // never un-hide job-filtered cards
      const effect = card.dataset.engravingEffect.toLowerCase()
      card.classList.toggle("hidden", term.length > 0 && !effect.includes(term))
    })
  }

  toggleMatchCharacter() {
    this._matchCharacter = !this._matchCharacter
    this.matchCharacterBtnTarget.classList.toggle("match-combatant-btn--active", this._matchCharacter)
    const characterJobs = (this.element.dataset.deckCharacterJobs || "").toLowerCase().split(",").filter(Boolean)
    this.filterEngravings(this.engravingModalState.cardType, characterJobs, this._matchCharacter)
  }

  renderEngravingPreview() {
    if (!this.hasEngravingPreviewTarget) return

    const { cardId, copyIndex, slot1EngravingId, slot2EngravingId, cardType } = this.engravingModalState

    const addedEl = this.addedCardTargets.find(el =>
      parseInt(el.dataset.cardId, 10) === cardId &&
      parseInt(el.dataset.copyIndex, 10) === 1
    )
    if (!addedEl) return

    const cardClone = addedEl.querySelector(".czn-card")?.cloneNode(true)
    if (!cardClone) return

    cardClone.querySelectorAll(".engraving-slot-icons").forEach(el => el.remove())

    const slot1Eidolon = slot1EngravingId ? this.getEngravingEidolon(slot1EngravingId, "slot1") : null
    const slot2Eidolon = slot2EngravingId ? this.getEngravingEidolon(slot2EngravingId, "slot2") : null

    const nameEl = cardClone.querySelector(".card-name")
    if (nameEl) nameEl.textContent = this.personaName(slot1Eidolon, slot2Eidolon)

    const newCost = this.calculateEngravingCost(cardId, slot1EngravingId, slot2EngravingId)
    const costEl = cardClone.querySelector(".card-cost")
    if (costEl && newCost !== null) costEl.textContent = newCost

    if (!slot1EngravingId && !slot2EngravingId) {
      const pickerCard = document.querySelector(`[data-card-picker-target="card"][data-card-id="${cardId}"]`)
      if (pickerCard) {
        const originalIllustration = pickerCard.querySelector(".card-illustration")
        const illustrationEl = cardClone.querySelector(".card-illustration")
        if (originalIllustration && illustrationEl) illustrationEl.src = originalIllustration.src
      }
    } else {
      const illustrationKey = this.personaIllustrationKey(cardType, slot1Eidolon, slot2Eidolon)
      if (illustrationKey) {
        const illustrationEl = cardClone.querySelector(".card-illustration")
        if (illustrationEl) {
          illustrationEl.src = this.element.dataset[`deckPersona${illustrationKey.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join("")}`] || illustrationEl.src
        }
      }
    }

    const luxPath = this.element.dataset.deckEidolonLux
    const umbraPath = this.element.dataset.deckEidolonUmbra
    const slotPath = this.element.dataset.deckEidolonSlot

    const iconPath = (eidolon) => {
      if (eidolon === "lux") return luxPath
      if (eidolon === "umbra") return umbraPath
      return slotPath
    }

    // Helper to get engraving card element
    const getEngCard = (engravingId, slot) => {
      if (!engravingId) return null
      const targets = slot === "slot1" ? this.engravingSlot1CardTargets : this.engravingSlot2CardTargets
      return targets.find(c => parseInt(c.dataset.engravingId, 10) === engravingId) || null
    }

    // Inject engraving talents into talent line
    const talentEl = cardClone.querySelector(".card-talent")
    const engravingTalents = []

    if (talentEl) {
      // Strip any previously injected engraving talents by re-reading from the picker card
      const pickerCard = document.querySelector(`[data-card-picker-target="card"][data-card-id="${cardId}"]`)
      if (pickerCard) {
        const originalTalentEl = pickerCard.querySelector(".card-talent")
        if (originalTalentEl) {
          talentEl.textContent = originalTalentEl.textContent
        }
      }
    }

    ;["slot1", "slot2"].forEach(slot => {
      const id = slot === "slot1" ? slot1EngravingId : slot2EngravingId
      const engCard = getEngCard(id, slot)
      if (!engCard) return
      const talents = engCard.dataset.talents ? engCard.dataset.talents.split(",").filter(Boolean) : []
      engravingTalents.push(...talents)
    })

    const uniqueTalents = [...new Set(engravingTalents)]
    if (talentEl && uniqueTalents.length > 0) {
      const current = talentEl.textContent.replace(/^\[|\]$/g, "").trim()
      const formatted = uniqueTalents.map(t => t.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()))
      talentEl.textContent = `[ ${current} / ${formatted.join(" / ")} ]`
    }

    // Inject engraving effects
    const effectEl = cardClone.querySelector("[data-epiphany-effects]")
    if (effectEl) {
      const effects = []

      ;["slot1", "slot2"].forEach(slot => {
        const id = slot === "slot1" ? slot1EngravingId : slot2EngravingId
        const engCard = getEngCard(id, slot)
        if (!engCard) return
        const eidolon = engCard.dataset.eidolon
        const effect = engCard.dataset.engravingEffect
        const eidolonIconPath = eidolon === "lux" ? luxPath : umbraPath
        effects.push(`
          <p class="engraving-added-effect">
            <img src="${eidolonIconPath}" class="engraving-effect-eidolon-icon">
            <span>${effect}</span>
          </p>
        `)
      })

      if (effects.length > 0) {
        effectEl.innerHTML = effects.join("")
        effectEl.classList.remove("hidden")
      } else {
        effectEl.innerHTML = ""
        effectEl.classList.add("hidden")
      }
    }

    const slotIconsEl = document.createElement("div")
    slotIconsEl.className = "engraving-slot-icons"
    slotIconsEl.innerHTML = `
      <img src="${slotPath}" class="engraving-slot-base">
      <img src="${iconPath(slot1Eidolon)}" class="engraving-slot-overlay engraving-slot-top">
      <img src="${iconPath(slot2Eidolon)}" class="engraving-slot-overlay engraving-slot-bottom">
    `
    cardClone.appendChild(slotIconsEl)

    this.engravingPreviewTarget.innerHTML = ""
    this.engravingPreviewTarget.appendChild(cardClone)
  }

  getEngravingEidolon(engravingId, slot) {
    const targets = slot === "slot1" ? this.engravingSlot1CardTargets : this.engravingSlot2CardTargets
    const card = targets.find(c => parseInt(c.dataset.engravingId, 10) === engravingId)
    return card ? card.dataset.eidolon : null
  }

  personaName(slot1Eidolon, slot2Eidolon) {
    const lux = [slot1Eidolon, slot2Eidolon].filter(e => e === "lux").length
    const umbra = [slot1Eidolon, slot2Eidolon].filter(e => e === "umbra").length
    if (lux === 2) return "Persona of Luster"
    if (umbra === 2) return "Persona of Obsidian"
    if (lux === 1 && umbra === 1) return "Persona of Boundary"
    if (lux === 1) return "Lux Persona"
    if (umbra === 1) return "Umbra Persona"
    return "Persona"
  }

  personaIllustrationKey(cardType, slot1Eidolon, slot2Eidolon) {
    const lux = [slot1Eidolon, slot2Eidolon].filter(e => e === "lux").length
    const umbra = [slot1Eidolon, slot2Eidolon].filter(e => e === "umbra").length
    if (lux === 2) return `persona_${cardType}_luster`
    if (umbra === 2) return `persona_${cardType}_obsidian`
    if (lux === 1 && umbra === 1) return `persona_${cardType}_boundary`
    if (lux === 1) return `persona_${cardType}_lux`
    if (umbra === 1) return `persona_${cardType}_umbra`
    return null
  }

  calculateEpiphanyCost(originalCardId, sourceType, divineEpiphanyId) {
    let baseCost

    if (sourceType === "added") {
      const pickerCard = document.querySelector(`[data-card-picker-target="card"][data-card-id="${originalCardId}"]`)
      baseCost = parseInt(pickerCard?.querySelector(".card-cost")?.textContent?.trim(), 10)
    } else {
      const slotEl = this.slotCardTargets.find(el =>
        parseInt(el.dataset.originalCardId, 10) === originalCardId &&
        parseInt(el.dataset.copyIndex, 10) === 1
      )
      baseCost = parseInt(slotEl?.dataset.baseCost, 10)
    }

    if (isNaN(baseCost)) return null

    let modifier = 0
    if (divineEpiphanyId) {
      const divineCard = this.modalDivineEpiphanyCardTargets.find(
        c => parseInt(c.dataset.epiphanyId, 10) === divineEpiphanyId
      )
      modifier += parseInt(divineCard?.dataset.costModifier || 0, 10)
    }

    return Math.max(0, baseCost + modifier)
  }

  // ============================================
  // CONFIRMATIONS
  // ============================================

  confirmCardSettings() {
    const { originalCardId, copyIndex, sourceType, selectedVariantId,
            selectedDungeon, divineEpiphanyId, epiphanyId } = this.modalState

    let copy

    if (sourceType === "added") {
      const added = this.deckState.addedCards.find(c => c.cardId === originalCardId)
      if (!added) return
      copy = added.copyCards.find(c => c.copyIndex === copyIndex)
      if (!copy) return

      copy.divineEpiphanyId = divineEpiphanyId || null
      copy.divineEpiphanyType = divineEpiphanyId ? selectedDungeon : null
      copy.epiphanyId = epiphanyId || null
      copy.epiphanyType = epiphanyId ? selectedDungeon : null
    } else {
      const slot = this.deckState.slots.find(s => s.originalCardId === originalCardId)
      if (!slot) return
      copy = slot.copyCards.find(c => c.copyIndex === copyIndex)
      if (!copy) return

      copy.currentCardId = selectedVariantId || originalCardId

      copy.divineEpiphanyId = divineEpiphanyId || null
      copy.divineEpiphanyType = divineEpiphanyId ? selectedDungeon : null

      const isVariant = copy.currentCardId !== originalCardId
      copy.epiphanyId = (!isVariant && epiphanyId) ? epiphanyId : null
      copy.epiphanyType = copy.epiphanyId ? selectedDungeon : null
    }

    this.cardSettingsModalTarget.classList.add("hidden")
    this.modalState = {}
    this.render()
  }

  clickOutsideCardSettings(event) {
    // Only close if clicking the backdrop itself, not the content inside
    if (event.target === this.cardSettingsModalTarget) {
      this.cancelCardSettings()
    }
  }

  selectVariantInModal(event) {
    event.stopPropagation()
    const variantId = parseInt(event.currentTarget.dataset.variantId, 10)
    const baseCardId = parseInt(event.currentTarget.dataset.baseCardId, 10)

    // Clicking the already-selected variant deselects back to base card
    if (this.modalState.selectedVariantId === variantId) {
      this.modalState.selectedVariantId = baseCardId
      this.modalState.isVariant = false
    } else {
      this.modalState.selectedVariantId = variantId
      this.modalState.isVariant = variantId !== baseCardId
    }

    // Highlight selected variant
    this.modalVariantOptionTargets.forEach(opt => {
      const id = parseInt(opt.dataset.variantId, 10)
      // Only highlight if it's a variant (not base card)
      const isSelected = id === this.modalState.selectedVariantId &&
                        this.modalState.isVariant
      opt.classList.toggle("modal-variant-selected", isSelected)
    })

    this.updateRegularEpiphanyAvailability()
    this.renderCardSettingsPreview()
  }

  selectDungeonInModal(event) {
    this.activateModalDungeon(event.currentTarget)
  }

  activateModalDungeon(button) {
    this.modalDungeonButtonTargets.forEach(btn => {
      btn.classList.remove("card-settings-dungeon-btn--active")
    })
    button.classList.add("card-settings-dungeon-btn--active")
    this.modalState.selectedDungeon = button.dataset.dungeon
    this.renderCardSettingsPreview()
  }

  selectDivineEpiphanyInModal(event) {
    event.stopPropagation()
    const epiphanyId = parseInt(event.currentTarget.dataset.epiphanyId, 10)

    // Toggle — clicking again deselects
    if (this.modalState.divineEpiphanyId === epiphanyId) {
      this.modalState.divineEpiphanyId = null
    } else {
      this.modalState.divineEpiphanyId = epiphanyId
    }

    // Highlight selected
    this.modalDivineEpiphanyCardTargets.forEach(card => {
      const id = parseInt(card.dataset.epiphanyId, 10)
      card.classList.toggle("modal-epiphany-selected", id === this.modalState.divineEpiphanyId)
    })

    this.renderCardSettingsPreview()
  }

  selectRegularEpiphanyInModal(event) {
    event.stopPropagation()

    // Disabled if variant selected
    if (this.modalState.isVariant) return

    const epiphanyId = parseInt(event.currentTarget.dataset.epiphanyId, 10)

    // Toggle — clicking again deselects
    if (this.modalState.epiphanyId === epiphanyId) {
      this.modalState.epiphanyId = null
    } else {
      this.modalState.epiphanyId = epiphanyId
    }

    // Highlight selected
    this.modalRegularEpiphanyCardTargets.forEach(card => {
      const id = parseInt(card.dataset.epiphanyId, 10)
      card.classList.toggle("modal-epiphany-selected", id === this.modalState.epiphanyId)
    })

    this.renderCardSettingsPreview()
  }

  filterModalEpiphaniesByCardType(cardType) {
    const allCards = [
      ...this.modalDivineEpiphanyCardTargets,
      ...this.modalRegularEpiphanyCardTargets
    ]

    allCards.forEach(card => {
      const epiphanyCardType = card.dataset.cardType
      const matches = epiphanyCardType === cardType ||
                      epiphanyCardType === "universal"
      if (matches) {
        card.classList.remove("type-hidden", "hidden")
      } else {
        card.classList.add("type-hidden", "hidden")
      }
    })
  }

  searchEpiphaniesInModal() {
    const searchTerm = this.modalEpiphanySearchTarget.value.toLowerCase()
    const allCards = [
      ...this.modalDivineEpiphanyCardTargets,
      ...this.modalRegularEpiphanyCardTargets
    ]

    allCards.forEach(card => {
      if (card.classList.contains("type-hidden")) return
      const effect = card.dataset.epiphanyEffect.toLowerCase()
      const matches = !searchTerm || effect.includes(searchTerm)
      card.classList.toggle("hidden", !matches)
    })
  }

  updateRegularEpiphanyAvailability() {
    const isVariant = this.modalState.isVariant

    if (this.hasRegularEpiphanySectionTarget) {
      this.regularEpiphanySectionTarget.classList.toggle(
        "card-settings-section-disabled", isVariant
      )
    }

    // Clear regular epiphany selection if variant was just picked
    if (isVariant && this.modalState.epiphanyId) {
      this.modalState.epiphanyId = null
      this.modalRegularEpiphanyCardTargets.forEach(card => {
        card.classList.remove("modal-epiphany-selected")
      })
    }
  }

  renderCardSettingsPreview() {
    if (!this.hasCardSettingsPreviewTarget) return

    const { originalCardId, copyIndex, sourceType, selectedVariantId,
            selectedDungeon, divineEpiphanyId, epiphanyId, isVariant } = this.modalState

    let cardClone

    if (sourceType === "added") {
      // Clone from the first added card DOM element
      const addedEl = this.addedCardTargets.find(el =>
        parseInt(el.dataset.cardId, 10) === originalCardId &&
        parseInt(el.dataset.copyIndex, 10) === 1
      )
      if (!addedEl) return
      cardClone = addedEl.querySelector(".czn-card")?.cloneNode(true)
    } else {
      const cardEl = this.slotCardTargets.find(el => {
        return parseInt(el.dataset.originalCardId, 10) === originalCardId &&
              parseInt(el.dataset.cardId, 10) === (selectedVariantId || originalCardId) &&
              parseInt(el.dataset.copyIndex, 10) === 1
      })
      if (!cardEl) return
      cardClone = cardEl.querySelector(".czn-card")?.cloneNode(true)
    }

    if (!cardClone) return

    // Swap neutral border for character element border on season/common added cards
    const characterElement = (this.element.dataset.deckCharacterElement || "").toLowerCase()
    if (characterElement && sourceType === "added") {
      const borderPath = this.element.dataset[`deckBorder${characterElement.charAt(0).toUpperCase() + characterElement.slice(1)}`]
      const borderEl = cardClone.querySelector(".card-element-border")
      if (borderEl && borderPath) borderEl.src = borderPath
    }

    if (copyIndex > 1) {
      const illustration = cardClone.querySelector(".card-illustration")
      if (illustration) illustration.style.transform = "scaleX(-1)"
      const video = cardClone.querySelector(".card-video")
      if (video) video.style.transform = "scaleX(-1)"
    }

    const iconEl = cardClone.querySelector("[data-epiphany-icon]")
    if (iconEl) {
      const defaultIcon = this.cardSettingsModalTarget.dataset.defaultEpiphanyIcon

      if (divineEpiphanyId && selectedDungeon) {
        const dungeonBtn = this.modalDungeonButtonTargets.find(
          b => b.dataset.dungeon === selectedDungeon
        )
        const overlayImg = dungeonBtn?.querySelector("[data-overlay]")
        if (overlayImg) {
          iconEl.innerHTML = `<img src="${overlayImg.src}" class="epiphany-icon-img" alt="${selectedDungeon}">`
          iconEl.classList.remove("hidden")
        }
      } else if (isVariant || epiphanyId) {
        if (defaultIcon) {
          iconEl.innerHTML = `<img src="${defaultIcon}" class="epiphany-icon-img" alt="epiphany">`
          iconEl.classList.remove("hidden")
        }
      } else {
        iconEl.innerHTML = ""
        iconEl.classList.add("hidden")
      }
    }

    // Update cost based on divine epiphany modifier
    const slotEl = this.slotCardTargets.find(el =>
      parseInt(el.dataset.originalCardId, 10) === originalCardId &&
      parseInt(el.dataset.cardId, 10) === (selectedVariantId || originalCardId) &&
      parseInt(el.dataset.copyIndex, 10) === 1
    )
    const baseCost = sourceType === "slot"
      ? parseInt(slotEl?.dataset.baseCost, 10)
      : parseInt(document.querySelector(`[data-card-picker-target="card"][data-card-id="${originalCardId}"]`)?.querySelector(".card-cost")?.textContent?.trim(), 10)
    const divineCard = divineEpiphanyId
      ? this.modalDivineEpiphanyCardTargets.find(c => parseInt(c.dataset.epiphanyId, 10) === divineEpiphanyId)
      : null
    const costModifier = parseInt(divineCard?.dataset.costModifier || 0, 10)
    const newCost = isNaN(baseCost) ? null : Math.max(0, baseCost + costModifier)
    const costEl = cardClone.querySelector(".card-cost")
    if (costEl && newCost !== null) costEl.textContent = newCost

    const effectEl = cardClone.querySelector("[data-epiphany-effects]")
    if (effectEl) {
      const effects = []

      if (epiphanyId && !isVariant) {
        const regularCard = this.modalRegularEpiphanyCardTargets.find(
          c => parseInt(c.dataset.epiphanyId, 10) === epiphanyId
        )
        if (regularCard) {
          effects.push(`<p class="epiphany-added-effect">${regularCard.dataset.epiphanyEffect}</p>`)
        }
      }

      if (divineEpiphanyId && selectedDungeon) {
        const divineCard = this.modalDivineEpiphanyCardTargets.find(
          c => parseInt(c.dataset.epiphanyId, 10) === divineEpiphanyId
        )
        if (divineCard) {
          const dungeonBtn = this.modalDungeonButtonTargets.find(
            b => b.dataset.dungeon === selectedDungeon
          )
          const itxtImg = dungeonBtn?.querySelector(".card-settings-dungeon-icon")
          const itxtSrc = itxtImg?.src ||
            this.element.dataset[`deckItxt${selectedDungeon.charAt(0).toUpperCase() + selectedDungeon.slice(1)}`] ||
            ""
          effects.push(`
            <p class="epiphany-added-effect epiphany-added-effect--divine">
              <img src="${itxtSrc}" class="epiphany-itxt-icon" alt="${selectedDungeon}">
              <span>${divineCard.dataset.epiphanyEffect.split('||').join(' ')}</span>
            </p>
          `)
        }
      }

      if (effects.length > 0) {
        effectEl.innerHTML = effects.join("")
        effectEl.classList.remove("hidden")
      } else {
        effectEl.innerHTML = ""
        effectEl.classList.add("hidden")
      }
    }

    this.cardSettingsPreviewTarget.innerHTML = ""
    this.cardSettingsPreviewTarget.appendChild(cardClone)

    const video = cardClone.querySelector("video")
    if (video) video.play().catch(() => {})
  }

  // Called when user clicks the CARD ITSELF (not the ↑ button)
  // Only opens variant picker if the card is variantable
  openVariantPickerFromCard(event) {
    const wrapper = event.currentTarget
    const variantable = wrapper.dataset.variantable === "true"
    if (!variantable) return

    const originalCardId = parseInt(wrapper.dataset.originalCardId, 10)
    const copyIndex = parseInt(wrapper.dataset.copyIndex, 10)

    this.variantTargetSlotOriginalId = originalCardId
    this.variantTargetCopyIndex = copyIndex

    this.variantSetTargets.forEach(set => {
      const setBaseId = parseInt(set.dataset.baseCardId, 10)
      set.classList.toggle("hidden", setBaseId !== originalCardId)
    })

    this.variantModalTarget.classList.remove("hidden")
  }

  initializeSlotsForCharacter(characterId) {
    this.deckState.characterId = characterId

    // Read base cards (copy-index 1, original card only) from injected slot cards
    const baseSlotCards = this.slotCardTargets.filter(el => {
      return parseInt(el.dataset.characterId, 10) === characterId &&
            parseInt(el.dataset.copyIndex, 10) === 1 &&
            parseInt(el.dataset.cardId, 10) === parseInt(el.dataset.originalCardId, 10)
    })

    this.deckState.slots = baseSlotCards.map((el, index) => {
      const cardId = parseInt(el.dataset.originalCardId, 10)
      return {
        slotIndex: index,
        originalCardId: cardId,
        origin: el.dataset.cardOrigin,
        unique: el.dataset.unique === "true",
        removed: false,
        copyCards: [{
          copyIndex: 1,
          currentCardId: cardId,
          divineEpiphanyId: null,
          divineEpiphanyType: null,
          epiphanyId: null,
          epiphanyType: null
        }]
      }
    })

    this.deckState.addedCards = []
  }

  addCombatantCopy(cardId) {
    const slot = this.deckState.slots.find(s =>
      s.copyCards.some(c => c.currentCardId === cardId)
    )

    if (!slot) {
      console.warn("[deck] Combatant card not found in slots:", cardId)
      return
    }

    // Unique cards cannot be copied
    if (slot.unique) {
      console.warn("[deck] Cannot copy Unique card:", cardId)
      return
    }

    if (slot.copyCards.length >= 4) {
      console.warn("[deck] Cannot add more copies — already at max (4)")
      return
    }

    if (this.combatantExtrasUsed() >= 3) {
      console.warn("[deck] Combatant copy budget exhausted (3 extras max)")
      return
    }

    const lastCopy = slot.copyCards[slot.copyCards.length - 1]
    const nextIndex = slot.copyCards.length + 1
    slot.copyCards.push({
      copyIndex: nextIndex,
      currentCardId: lastCopy.currentCardId,
      divineEpiphanyId: lastCopy.divineEpiphanyId,
      divineEpiphanyType: lastCopy.divineEpiphanyType,
      epiphanyId: lastCopy.epiphanyId,
      epiphanyType: lastCopy.epiphanyType
    })
  }

  addOutsideCard(cardId) {
    const existing = this.deckState.addedCards.find(c => c.cardId === cardId)

    if (existing) {
      if (existing.unique) {
        console.warn("[deck] Cannot copy Unique card:", cardId)
        return
      }
      if (existing.copyCards.length >= 4) {
        console.warn("[deck] Cannot add more copies of outside card:", cardId)
        return
      }
      if (this.combatantExtrasUsed() >= 3) {
        console.warn("[deck] Copy budget exhausted")
        return
      }
      const nextIndex = existing.copyCards.length + 1
      existing.copyCards.push({
        copyIndex: nextIndex,
        divineEpiphanyId: null,
        divineEpiphanyType: null,
        epiphanyId: null,
        epiphanyType: null
      })
      this.createAddedCardElement(cardId, nextIndex, existing.category, existing.rarity, existing.unique)
    } else {
      const cardEl = document.querySelector(
        `[data-card-picker-target="card"][data-card-id="${cardId}"]`
      )
      const category = cardEl ? cardEl.dataset.cardCategory : null
      const rarity = cardEl ? cardEl.dataset.cardRarity : null
      const canEpiphany = cardEl ? cardEl.dataset.canEpiphany : "false"
      const canEngrave = cardEl ? cardEl.dataset.canEngrave === "true" : false
      const cardType = cardEl ? cardEl.dataset.cardCardType : null
      const unique = cardEl ? cardEl.dataset.unique === "true" : false

      this.deckState.addedCards.push({
        cardId,
        category,
        rarity,
        canEpiphany,
        canEngrave,
        cardType,
        unique,
        copyCards: [{
          copyIndex: 1,
          divineEpiphanyId: null,
          divineEpiphanyType: null,
          epiphanyId: null,
          epiphanyType: null
        }]
      })
      this.createAddedCardElement(cardId, 1, category, rarity, unique)
    }
  }

  createAddedCardElement(cardId, copyIndex, category, rarity, unique = false) {
    const pickerCard = document.querySelector(
      `[data-card-picker-target="card"][data-card-id="${cardId}"]`
    )
    if (!pickerCard) return

    const canEpiphany = pickerCard.dataset.canEpiphany || "false"
    const canEngrave = pickerCard.dataset.canEngrave || "false"
    const cardCardType = pickerCard.dataset.cardCardType || ""

    const wrapper = document.createElement("div")
    wrapper.setAttribute("data-deck-target", "addedCard")
    wrapper.setAttribute("data-card-id", cardId)
    wrapper.setAttribute("data-original-card-id", cardId)
    wrapper.setAttribute("data-copy-index", copyIndex)
    wrapper.setAttribute("data-card-type", "added")
    wrapper.setAttribute("data-card-category", category)
    wrapper.setAttribute("data-card-rarity", rarity)
    wrapper.setAttribute("data-can-epiphany", canEpiphany)
    wrapper.setAttribute("data-can-engrave", canEngrave)
    wrapper.setAttribute("data-card-card-type", cardCardType)
    wrapper.setAttribute("data-variantable", "false")
    wrapper.setAttribute("data-action", "click->deck#openCardSettingsModal")
    wrapper.className = "deck-added-card"

    const cardVisual = pickerCard.querySelector(".czn-card")
    if (cardVisual) {
      const clone = cardVisual.cloneNode(true)
      if (copyIndex > 1) {
        const illustration = clone.querySelector(".card-illustration")
        if (illustration) illustration.style.transform = "scaleX(-1)"
        const video = clone.querySelector(".card-video")
        if (video) video.style.transform = "scaleX(-1)"
        const overlayPath = this.element.dataset.deckCardCopyOverlayPath
        if (overlayPath) {
          const overlay = document.createElement("img")
          overlay.src = overlayPath
          overlay.className = "card-copy-overlay"
          clone.appendChild(overlay)
        }
      }
      wrapper.appendChild(clone)
    }

    const duplicateBtn = document.createElement("button")
    duplicateBtn.type = "button"
    duplicateBtn.setAttribute("data-action", "click->deck#duplicateAddedCard")
    duplicateBtn.className = "deck-btn deck-btn-duplicate"
    duplicateBtn.textContent = "O"
    if (unique) duplicateBtn.classList.add("hidden")
    wrapper.appendChild(duplicateBtn)

    const removeBtn = document.createElement("button")
    removeBtn.type = "button"
    removeBtn.setAttribute("data-action", "click->deck#removeCard")
    removeBtn.className = "deck-btn deck-btn-remove"
    removeBtn.textContent = "X"
    wrapper.appendChild(removeBtn)

    const cardPicker = document.querySelector("[data-controller='card-picker']")

    // Insert after the last existing element for this card, or in order
    const existingEls = this.addedCardTargets.filter(el => parseInt(el.dataset.cardId, 10) === cardId)
    if (existingEls.length > 0) {
      existingEls[existingEls.length - 1].insertAdjacentElement("afterend", wrapper)
    } else {
      const cardIndex = this.deckState.addedCards.findIndex(c => c.cardId === cardId)
      if (cardIndex > 0) {
        const prevCardId = this.deckState.addedCards[cardIndex - 1].cardId
        const prevCardEls = this.addedCardTargets.filter(el => parseInt(el.dataset.cardId, 10) === prevCardId)
        if (prevCardEls.length > 0) {
          prevCardEls[prevCardEls.length - 1].insertAdjacentElement("afterend", wrapper)
        } else {
          cardPicker.parentElement.insertBefore(wrapper, cardPicker)
        }
      } else {
        cardPicker.parentElement.insertBefore(wrapper, cardPicker)
      }
    }
  }

  duplicateAddedCard(event) {
    event.stopPropagation()
    const wrapper = event.currentTarget.closest("[data-original-card-id]")
    const cardId = parseInt(wrapper.dataset.cardId, 10)
    const copyIndex = parseInt(wrapper.dataset.copyIndex, 10)

    const added = this.deckState.addedCards.find(c => c.cardId === cardId)
    if (!added) return

    if (added.unique) {
      console.warn("[deck] Cannot copy Unique card:", cardId)
      return
    }

    if (added.copyCards.length >= 4) {
      console.warn("[deck] Cannot add more copies — already at max (4)")
      return
    }

    if (this.combatantExtrasUsed() >= 3) {
      console.warn("[deck] Copy budget exhausted")
      return
    }

    const sourceCopy = added.copyCards.find(c => c.copyIndex === copyIndex)
    if (!sourceCopy) return

    const nextIndex = added.copyCards.length + 1
    added.copyCards.push({
      copyIndex: nextIndex,
      divineEpiphanyId: sourceCopy.divineEpiphanyId,
      divineEpiphanyType: sourceCopy.divineEpiphanyType,
      epiphanyId: sourceCopy.epiphanyId,
      epiphanyType: sourceCopy.epiphanyType,
      engraving1Id: sourceCopy.engraving1Id,
      engraving2Id: sourceCopy.engraving2Id
    })

    this.createAddedCardElement(cardId, nextIndex, added.category, added.rarity, added.unique)
    this.render()
  }

  removeSlotCopy(originalCardId, copyIndex) {
    const slot = this.deckState.slots.find(s => s.originalCardId === originalCardId)
    if (!slot || slot.removed) return

    if (slot.copyCards.length > 1) {
      // Remove the specific copy — doesn't count toward removal limit
      slot.copyCards = slot.copyCards.filter(c => c.copyIndex !== copyIndex)

      // Re-index remaining copies sequentially
      slot.copyCards = slot.copyCards.map((c, i) => ({
        ...c,
        copyIndex: i + 1
      }))
    } else {
      // Removing the last (original) copy — counts toward removal limit
      if (this.removalsUsed() >= 5) {
        console.warn("[deck] Removal limit reached (5 max)")
        return
      }
      slot.removed = true
      slot.copyCards = []
    }
  }

  duplicateSlotCopy(originalCardId, copyIndex) {
    const slot = this.deckState.slots.find(s => s.originalCardId === originalCardId)
    if (!slot || slot.removed) return

    // Unique cards cannot be copied
    if (slot.unique) {
      console.warn("[deck] Cannot copy Unique card:", originalCardId)
      return
    }

    if (slot.copyCards.length >= 4) {
      console.warn("[deck] Cannot add more copies — already at max (4)")
      return
    }

    if (this.combatantExtrasUsed() >= 3) {
      console.warn("[deck] Combatant copy budget exhausted (3 extras max)")
      return
    }

    const sourceCopy = slot.copyCards.find(c => c.copyIndex === copyIndex)
    if (!sourceCopy) return

    const nextIndex = slot.copyCards.length + 1
    slot.copyCards.push({
      copyIndex: nextIndex,
      currentCardId: sourceCopy.currentCardId,
      divineEpiphanyId: sourceCopy.divineEpiphanyId,
      divineEpiphanyType: sourceCopy.divineEpiphanyType,
      epiphanyId: sourceCopy.epiphanyId,
      epiphanyType: sourceCopy.epiphanyType
    })
  }

  removeAddedCopy(cardId, copyIndex) {
    const existing = this.deckState.addedCards.find(c => c.cardId === cardId)
    if (!existing) return

    if (existing.copyCards.length > 1) {
      existing.copyCards = existing.copyCards.filter(c => c.copyIndex !== copyIndex)
      existing.copyCards = existing.copyCards.map((c, i) => ({ ...c, copyIndex: i + 1 }))

      const els = this.addedCardTargets.filter(el => parseInt(el.dataset.cardId, 10) === cardId)
      const insertBefore = els[els.length - 1].nextSibling
      const parent = els[0].parentNode

      els.forEach(el => el.remove())

      existing.copyCards.forEach(copy => {
        this.createAddedCardElement(cardId, copy.copyIndex, existing.category, existing.rarity, existing.unique)
        const newEl = this.addedCardTargets[this.addedCardTargets.length - 1]
        parent.insertBefore(newEl, insertBefore)
      })
    } else {
      this.deckState.addedCards = this.deckState.addedCards.filter(c => c.cardId !== cardId)
      this.addedCardTargets
        .filter(el => parseInt(el.dataset.cardId, 10) === cardId)
        .forEach(el => el.remove())
    }
  }

  assignEpiphany(originalCardId, copyIndex, type, epiphanyId, epiphanyType) {
    const slot = this.deckState.slots.find(s => s.originalCardId === originalCardId)
    if (!slot) return

    const copy = slot.copyCards.find(c => c.copyIndex === copyIndex)
    if (!copy) return

    if (type === "divine") {
      copy.divineEpiphanyId = epiphanyId
      copy.divineEpiphanyType = epiphanyType
    } else {
      copy.epiphanyId = epiphanyId
      copy.epiphanyType = epiphanyType
    }

    this.render()
  }

  clearEpiphany(originalCardId, copyIndex, type) {
    const slot = this.deckState.slots.find(s => s.originalCardId === originalCardId)
    if (!slot) return

    const copy = slot.copyCards.find(c => c.copyIndex === copyIndex)
    if (!copy) return

    if (type === "divine") {
      copy.divineEpiphanyId = null
      copy.divineEpiphanyType = null
    } else {
      copy.epiphanyId = null
      copy.epiphanyType = null
    }

    this.render()
  }

  // ----- Computed values -----

  combatantExtrasUsed() {
    const slotExtras = this.deckState.slots.reduce((sum, slot) => {
      return sum + Math.max(0, slot.copyCards.length - 1)
    }, 0)
    const addedExtras = this.deckState.addedCards.reduce((sum, added) => {
      return sum + Math.max(0, added.copyCards.length - 1)
    }, 0)
    return slotExtras + addedExtras
  }

  combatantExtrasRemaining() {
    return Math.max(0, 3 - this.combatantExtrasUsed())
  }

  removalsUsed() {
    return this.deckState.slots.filter(s => s.removed).length
  }

  removalsRemaining() {
    return Math.max(0, 5 - this.removalsUsed())
  }

  calculateFaintMemory() {
    let points = 0

    // Slot points
    this.deckState.slots.forEach(slot => {
      if (slot.removed) {
        if (slot.origin === "start") points += 20
      }
      if (slot.copyCards.length === 4) points += 40
      slot.copyCards.forEach(copy => {
        if (copy.divineEpiphanyId) points += 20
      })
    })

    // Added outside card points — per copy
    this.deckState.addedCards.forEach(added => {
      const pointsPerCopy = this.outsideCardPoints(added.category, added.rarity)
      added.copyCards.forEach(copy => {
        points += pointsPerCopy
        if (copy.divineEpiphanyId) points += 20
      })
    })

    // Equipment enhancement points
    const enhancements = this.deckState.equipmentEnhancements || {}
    const hasReinforcement = Object.values(enhancements).some(e => e?.reinforcement_id)
    const hasGodHammer = Object.values(enhancements).some(e => e?.god_hammer_id)
    if (hasReinforcement) points += 10
    if (hasGodHammer) points += 10

    return points
  }

  outsideCardPoints(category, rarity) {
    if (category === "monster") {
      if (rarity === "normal") return 20
      if (rarity === "rare")   return 50
      if (rarity === "legend") return 80
      return 20  // fallback
    }
    if (category === "common")  return 20
    if (category === "season")  return 20
    if (category === "other")   return 20
    return 0
  }


  // ----- Rendering -----

  render() {
    const characterId = this.deckState.characterId
    const characterPicked = characterId !== null

    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.classList.toggle("hidden", characterPicked)
    }

    // Build a map: "originalCardId-copyIndex-currentCardId" → true
    // Only active copies go in this map
    const visibleCombinations = new Set()
    this.deckState.slots
      .filter(s => !s.removed)
      .forEach(slot => {
        slot.copyCards.forEach(copy => {
          visibleCombinations.add(
            `${slot.originalCardId}-${copy.copyIndex}-${copy.currentCardId}`
          )
        })
      })

    // Show slot cards that match an active combination
    this.slotCardTargets.forEach(card => {
      const cardCharacterId = parseInt(card.dataset.characterId, 10)
      const cardId = parseInt(card.dataset.cardId, 10)
      const originalCardId = parseInt(card.dataset.originalCardId, 10)
      const copyIndex = parseInt(card.dataset.copyIndex, 10)

      const key = `${originalCardId}-${copyIndex}-${cardId}`
      const visible = cardCharacterId === characterId &&
                      visibleCombinations.has(key)

      card.classList.toggle("hidden", !visible)
    })

    this.addedCardTargets.forEach(card => {
      const cardId = parseInt(card.dataset.cardId, 10)
      const copyIndex = parseInt(card.dataset.copyIndex, 10)
      const addedCard = this.deckState.addedCards.find(c => c.cardId === cardId)
      const visible = addedCard !== undefined && copyIndex <= addedCard.copyCards.length
      card.classList.toggle("hidden", !visible)
    })

    this.renderUndoList()
    this.updateCardEpiphanyVisuals()
    this.updateCardEngravingVisuals()
    this.updateAddedCardBorders()
    this.renderFaintMemory()
    this.renderBudgetCount()

    const cardCount = this.deckState.slots
                        .filter(s => !s.removed)
                        .reduce((sum, slot) => sum + slot.copyCards.length, 0) +
                      this.deckState.addedCards.reduce((sum, c) => sum + c.copyCards.length, 0)
    if (this.hasCardCountTarget) this.cardCountTarget.textContent = cardCount

    const removeCount = this.deckState.slots.filter(s => s.removed).length
    if (this.hasRemoveCountTarget) this.removeCountTarget.textContent = removeCount
  }


  renderUndoList() {
    if (!this.hasUndoListTarget) return

    const removedSlots = this.deckState.slots.filter(s => s.removed)

    if (removedSlots.length === 0) {
      this.undoListTarget.classList.add("hidden")
      return
    }

    this.undoListTarget.classList.remove("hidden")

    const cardNames = this.getCardNames()
    this.undoListTarget.innerHTML = `
      <p class="undo-list-title">Removed cards (${removedSlots.length}/5):</p>
      <div class="undo-items">
        ${removedSlots.map(slot => `
          <div class="undo-item">
            <span class="undo-card-name">
              ${cardNames[slot.originalCardId] || "Card #" + slot.originalCardId}
            </span>
            <button type="button"
                    class="undo-restore-button"
                    data-action="click->deck#restoreSlot"
                    data-original-card-id="${slot.originalCardId}">
              Restore
            </button>
          </div>
        `).join("")}
      </div>
    `
  }

  // ask is this is placed in the right place
  renderFaintMemory() {
    if (!this.hasFaintMemoryScoreTarget) return
    const points = this.calculateFaintMemory()
    this.faintMemoryScoreTarget.textContent = points
  }

  renderBudgetCount() {
    if (!this.hasBudgetCountTarget) return
    this.budgetCountTarget.textContent = this.combatantExtrasUsed()
  }

  getCardNames() {
    const names = {}
    this.slotCardTargets.forEach(card => {
      const originalCardId = parseInt(card.dataset.originalCardId, 10)
      if (!names[originalCardId]) {
        const nameEl = card.querySelector(".card-name")
        if (nameEl) names[originalCardId] = nameEl.textContent.trim()
        else names[originalCardId] = `Card #${originalCardId}`
      }
    })
    // Fill in names from deckState for removed slots not in DOM
    this.deckState.slots.filter(s => s.removed).forEach(slot => {
      if (!names[slot.originalCardId] && slot.cardName) {
        names[slot.originalCardId] = slot.cardName
      }
    })
    return names
  }

  // ============================================
  // STYLE PICKER
  // ============================================

  openStylePicker() {
    this.stylePickerModalTarget.classList.remove("hidden")
  }

  closeStylePicker() {
    this.stylePickerModalTarget.classList.add("hidden")
  }

  clickOutsideStylePicker(e) {
    if (e.target === this.stylePickerModalTarget) this.closeStylePicker()
  }

  selectPresetStyle(e) {
    const key = e.currentTarget.dataset.styleKey
    const assetPath = e.currentTarget.dataset.assetPath

    document.getElementById("style-key-input").value = key
    document.getElementById("remove-custom-style-input").value = "0"

    this.styleOptionTargets.forEach(opt => {
      opt.classList.toggle("selected", opt.dataset.styleKey === key)
    })

    this.stylePreviewTarget.style.backgroundImage = `url('${assetPath}')`
    this.stylePreviewLabelTarget.classList.add("hidden")
    this.closeStylePicker()
  }

  handleStyleUpload(e) {
    const file = e.target.files[0]
    if (!file) return
    if (file.size > 500 * 1024) {
      alert("Image must be under 500kb.")
      e.target.value = ""
      return
    }
    document.getElementById("style-key-input").value = ""
    this.styleOptionTargets.forEach(opt => opt.classList.remove("selected"))
    const reader = new FileReader()
    reader.onload = (ev) => {
      this.stylePreviewTarget.style.backgroundImage = `url('${ev.target.result}')`
      this.stylePreviewLabelTarget.classList.add("hidden")
    }
    reader.readAsDataURL(file)
    this.closeStylePicker()
  }

  clearStyle() {
    document.getElementById("style-key-input").value = ""
    document.getElementById("remove-custom-style-input").value = "1"
    this.styleOptionTargets.forEach(opt => opt.classList.remove("selected"))
    this.stylePreviewTarget.style.backgroundImage = ""
    this.stylePreviewLabelTarget.classList.remove("hidden")
    this.closeStylePicker()
  }

  // ============================================
  // EQUIPMENT ENHANCEMENT MODAL
  // ============================================

  openEquipmentEnhancementModal(event) {
    event.stopPropagation()
    const slot = event.currentTarget.dataset.slot
    this.enhancementModalState = {
      slot,
      engraving_id: this.deckState.equipmentEnhancements?.[slot]?.engraving_id || null,
      reinforcement_id: this.deckState.equipmentEnhancements?.[slot]?.reinforcement_id || null,
      god_hammer_id: this.deckState.equipmentEnhancements?.[slot]?.god_hammer_id || null
    }

    // Open to the season that has active selections, default to season 2
    const hasSeason1 = this.enhancementModalState.reinforcement_id || this.enhancementModalState.god_hammer_id
    this.switchToEnhancementSeason(hasSeason1 ? 1 : 2)

    this.highlightEnhancementSelections()
    this.updateEnhancementSummary()

    this.equipmentEnhancementModalTarget.classList.remove("hidden")
  }

  cancelEquipmentEnhancement() {
    this.equipmentEnhancementModalTarget.classList.add("hidden")
    this.enhancementModalState = {}
  }

  clickOutsideEquipmentEnhancementModal(event) {
    if (event.target === this.equipmentEnhancementModalTarget) {
      this.cancelEquipmentEnhancement()
    }
  }

  switchEnhancementSeason(event) {
    const season = parseInt(event.currentTarget.dataset.season)
    this.switchToEnhancementSeason(season)
  }

  switchToEnhancementSeason(season) {
    // Update tabs
    this.enhancementSeasonTabTargets.forEach(tab => {
      tab.classList.toggle("enhancement-season-tab--active", parseInt(tab.dataset.season) === season)
    })

    // Show/hide panels
    this.enhancementSeason1PanelTarget.classList.toggle("hidden", season !== 1)
    this.enhancementSeason2PanelTarget.classList.toggle("hidden", season !== 2)
  }

  selectEquipmentEnhancement(event) {
    event.stopPropagation()
    const card = event.currentTarget
    const id = parseInt(card.dataset.enhancementId)
    const type = card.dataset.enhancementType

    if (type === "engraving") {
      // Toggle — clicking again deselects
      this.enhancementModalState.engraving_id = this.enhancementModalState.engraving_id === id ? null : id
      // Clear season 1 if season 2 selected
      if (this.enhancementModalState.engraving_id) {
        this.enhancementModalState.reinforcement_id = null
        this.enhancementModalState.god_hammer_id = null
      }
    } else if (type === "arena_reinforcement") {
      this.enhancementModalState.reinforcement_id = this.enhancementModalState.reinforcement_id === id ? null : id
      // Clear season 2 if season 1 selected
      if (this.enhancementModalState.reinforcement_id) {
        this.enhancementModalState.engraving_id = null
      }
    } else if (type === "god_hammer") {
      this.enhancementModalState.god_hammer_id = this.enhancementModalState.god_hammer_id === id ? null : id
      // Clear season 2 if season 1 selected
      if (this.enhancementModalState.god_hammer_id) {
        this.enhancementModalState.engraving_id = null
      }
    }

    this.highlightEnhancementSelections()
    this.updateEnhancementSummary()
  }

  highlightEnhancementSelections() {
    const { engraving_id, reinforcement_id, god_hammer_id } = this.enhancementModalState

    this.equipmentEnhancementCardTargets.forEach(card => {
      const id = parseInt(card.dataset.enhancementId)
      const type = card.dataset.enhancementType
      let selected = false
      if (type === "engraving") selected = id === engraving_id
      if (type === "arena_reinforcement") selected = id === reinforcement_id
      if (type === "god_hammer") selected = id === god_hammer_id
      card.classList.toggle("modal-epiphany-selected", selected)
    })
  }

  updateEnhancementSummary() {
    if (!this.hasEnhancementSummaryTarget) return
    const { engraving_id, reinforcement_id, god_hammer_id } = this.enhancementModalState
    const lines = []

    if (engraving_id) {
      const card = this.equipmentEnhancementCardTargets.find(c => parseInt(c.dataset.enhancementId) === engraving_id)
      if (card) lines.push(`<p class="text-sm"><span class="opacity-50">Engraving:</span> ${card.dataset.enhancementEffect}</p>`)
    }
    if (reinforcement_id) {
      const card = this.equipmentEnhancementCardTargets.find(c => parseInt(c.dataset.enhancementId) === reinforcement_id)
      if (card) lines.push(`<p class="text-sm"><span class="opacity-50">Reinforcement:</span> ${card.dataset.enhancementEffect}</p>`)
    }
    if (god_hammer_id) {
      const card = this.equipmentEnhancementCardTargets.find(c => parseInt(c.dataset.enhancementId) === god_hammer_id)
      if (card) lines.push(`<p class="text-sm"><span class="opacity-50">God Hammer:</span> ${card.dataset.enhancementEffect}</p>`)
    }

    this.enhancementSummaryTarget.innerHTML = lines.length > 0
      ? lines.join("")
      : `<p class="text-sm opacity-50">No enhancement selected</p>`
  }

  confirmEquipmentEnhancement() {
    const { slot, engraving_id, reinforcement_id, god_hammer_id } = this.enhancementModalState

    if (!this.deckState.equipmentEnhancements) this.deckState.equipmentEnhancements = {}
    this.deckState.equipmentEnhancements[slot] = {
      engraving_id: engraving_id || null,
      reinforcement_id: reinforcement_id || null,
      god_hammer_id: god_hammer_id || null
    }

    this.equipmentEnhancementModalTarget.classList.add("hidden")
    this.enhancementModalState = {}
    this.updateEnhancementIcons()
    this.renderFaintMemory()
  }

  searchEquipmentEnhancements() {
    const term = this.equipmentEnhancementSearchTarget.value.toLowerCase()
    this.equipmentEnhancementCardTargets.forEach(card => {
      const effect = card.dataset.enhancementEffect.toLowerCase()
      card.classList.toggle("hidden", term.length > 0 && !effect.includes(term))
    })
  }

  updateEnhancementIcons() {
    this.enhancementTriggerTargets.forEach(trigger => {
      const slot = trigger.dataset.slot
      const enhancements = this.deckState.equipmentEnhancements?.[slot] || {}
      const img = trigger.querySelector("img")
      if (!img) return

      // Reset hammer class
      // Reset classes
      img.classList.remove("enhancement-trigger-icon--hammer", "enhancement-trigger-icon--reinforcement")

      if (enhancements.god_hammer_id) {
        const card = this.equipmentEnhancementCardTargets.find(c => parseInt(c.dataset.enhancementId) === enhancements.god_hammer_id)
        const rarity = card?.dataset.enhancementRarity || "legend"
        img.src = this.element.dataset[`deckHammer${rarity.charAt(0).toUpperCase() + rarity.slice(1)}`] || img.src
        img.classList.add("enhancement-trigger-icon--hammer")
      } else if (enhancements.reinforcement_id) {
        img.src = this.element.dataset.deckEnhancementReinforcement || img.src
        img.classList.add("enhancement-trigger-icon--reinforcement")
      } else if (enhancements.engraving_id) {
        const card = this.equipmentEnhancementCardTargets.find(c => parseInt(c.dataset.enhancementId) === enhancements.engraving_id)
        const name = card?.dataset.enhancementName?.toLowerCase()
        if (name === "lux") img.src = this.element.dataset.deckEnhancementLux || img.src
        if (name === "umbra") img.src = this.element.dataset.deckEnhancementUmbra || img.src
      } else {
        img.src = this.element.dataset.deckEnhancementInteraction || img.src
      }
    })
  }

  calculateEngravingCost(baseCardId, engraving1Id, engraving2Id) {
    const pickerCard = document.querySelector(`[data-card-picker-target="card"][data-card-id="${baseCardId}"]`)
    if (!pickerCard) return null
    const baseCost = parseInt(pickerCard.querySelector(".card-cost")?.textContent?.trim(), 10)
    if (isNaN(baseCost)) return null

    let modifier = 0
    if (engraving1Id) {
      const eng1 = this.engravingSlot1CardTargets.find(c => parseInt(c.dataset.engravingId, 10) === engraving1Id)
      modifier += parseInt(eng1?.dataset.costModifier || 0, 10)
    }
    if (engraving2Id) {
      const eng2 = this.engravingSlot2CardTargets.find(c => parseInt(c.dataset.engravingId, 10) === engraving2Id)
      modifier += parseInt(eng2?.dataset.costModifier || 0, 10)
    }

    return baseCost + modifier
  }

}
